import React, { useState, useEffect } from "react";
import {
	Box,
	Button,
	Card,
	Chip,
	Container,
	FormControl,
	IconButton,
	InputAdornment,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import ConfirmationNumberTwoToneIcon from "@mui/icons-material/ConfirmationNumberTwoTone";
import QrCodeScannerTwoToneIcon from "@mui/icons-material/QrCodeScannerTwoTone";
import HistoryTwoToneIcon from "@mui/icons-material/HistoryTwoTone";
import AddCircleTwoToneIcon from "@mui/icons-material/AddCircleTwoTone";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { CommonPagination } from "src/common/CommonPagination";
import { useGetQueryWithTotal } from "src/hooks/useGetQueryWithTotal";
import { useDebounce } from "src/hooks/useDebounce";
import type { ITicketListItem } from "../type/ticketScanType";
import TicketListTable from "./TicketListTable";
import TicketWiseScanModal from "./TicketWiseScanModal";
import UpdateTicketModal from "./UpdateTicketModal";
import DeleteTicketModal from "./DeleteTicketModal";

const PAYMENT_METHODS = [
	{ value: "cash", label: "Cash" },
	{ value: "online", label: "Online" },

];

const TicketListPage: React.FC = () => {
	const [ticketPagination, setTicketPagination] = useState({
		limit: 50,
		skip: 0,
	});
	const [ticketSearch, setTicketSearch] = useState<string>("");
	const debouncedTicketSearch = useDebounce(ticketSearch, 500);
	const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>("");
	const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>("");

	useEffect(() => {
		setTicketPagination((prev) => ({ ...prev, skip: 0 }));
	}, [debouncedTicketSearch, paymentStatusFilter, paymentMethodFilter]);

	const [ticketScanModal, setTicketScanModal] = useState<{
		open: boolean;
		ticket: ITicketListItem | null;
	}>({
		open: false,
		ticket: null,
	});

	const [updateTicketModal, setUpdateTicketModal] = useState<{
		open: boolean;
		ticket: ITicketListItem | null;
	}>({
		open: false,
		ticket: null,
	});

	const [deleteTicketModal, setDeleteTicketModal] = useState<{
		open: boolean;
		ticket: ITicketListItem | null;
	}>({
		open: false,
		ticket: null,
	});


	const { data: ticketsData, isLoading: isTicketsLoading } =
		useGetQueryWithTotal<ITicketListItem[]>({
			endPoint: "/api/v1/admin/visitor/ticket",
			queryKeys: [
				"AllVisitorTickets",
				ticketPagination.skip,
				ticketPagination.limit,
				paymentStatusFilter,
				paymentMethodFilter,
				debouncedTicketSearch,
			],
			params: {
				limit: ticketPagination.limit,
				skip: ticketPagination.skip,
				...(paymentStatusFilter ? { payment_status: paymentStatusFilter } : {}),
				...(paymentMethodFilter ? { payment_method: paymentMethodFilter } : {}),
				...(debouncedTicketSearch.trim()
					? {
						searchQuery: debouncedTicketSearch.trim(),
					}
					: {}),
			},
		});

	const hasActiveFilters = Boolean(
		ticketSearch || paymentStatusFilter || paymentMethodFilter
	);

	const handleClearFilters = () => {
		setTicketSearch("");
		setPaymentStatusFilter("");
		setPaymentMethodFilter("");
	};

	return (
		<>
			<Helmet>
				<title>BTTF - Ticket List</title>
			</Helmet>

			<Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
				{/* Page Header */}
				<Box
					sx={{
						mb: 3,
						display: "flex",
						flexDirection: { xs: "column", sm: "row" },
						alignItems: { xs: "flex-start", sm: "center" },
						justifyContent: "space-between",
						gap: 2,
					}}
				>
					<Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
							<ConfirmationNumberTwoToneIcon />
							<Typography variant="h3" fontWeight={700}>
								Ticket List
							</Typography>
						</Box>
					</Box>

					{/* Header Action Buttons */}
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
						<Button
							component={RouterLink}
							to="/visitor/create-ticket"
							variant="contained"
							color="primary"
							startIcon={<AddCircleTwoToneIcon />}
							sx={{
								borderRadius: 1.5,
								textTransform: "none",
								fontWeight: 700,
								boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
							}}
						>
							Create Ticket
						</Button>

						<Button
							component={RouterLink}
							to="/visitor/scan-ticket"
							variant="outlined"
							color="primary"
							startIcon={<QrCodeScannerTwoToneIcon />}
							sx={{
								borderRadius: 1.5,
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							Scan Ticket
						</Button>

						<Button
							component={RouterLink}
							to="/visitor/scan-history"
							variant="outlined"
							color="inherit"
							startIcon={<HistoryTwoToneIcon />}
							sx={{
								borderRadius: 1.5,
								textTransform: "none",
								fontWeight: 600,
							}}
						>
							Scan History
						</Button>
					</Box>
				</Box>

				{/* Main Card */}
				<Card elevation={2} sx={{ overflow: "hidden", borderRadius: 2 }}>
					{/* Filter Bar */}
					<Box
						sx={{
							p: 2,
							borderBottom: "1px solid",
							borderColor: "divider",
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: { xs: "stretch", sm: "flex-end" },
								alignItems: "center",
								flexWrap: "wrap",
								gap: 1.5,
							}}
						>
							{/* Search Field */}
							<TextField
								size="small"
								placeholder="Search ticket no"
								value={ticketSearch}
								onChange={(e) => setTicketSearch(e.target.value)}
								sx={{ width: { xs: "100%", sm: 220, md: 240 } }}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon fontSize="small" color="action" />
										</InputAdornment>
									),
									endAdornment: ticketSearch ? (
										<InputAdornment position="end">
											<IconButton
												type="button"
												size="small"
												aria-label="clear search"
												onClick={() => setTicketSearch("")}
												edge="end"
												sx={{ p: 0.5 }}
											>
												<ClearIcon fontSize="small" />
											</IconButton>
										</InputAdornment>
									) : null,
								}}
							/>

							{/* Payment Status Filter */}
							<FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 150 } }}>
								<Select
									value={paymentStatusFilter}
									onChange={(e) => setPaymentStatusFilter(e.target.value)}
									displayEmpty
									renderValue={(val) => {
										if (!val) return "All Payment Status";
										return val === "paid"
											? "Paid"
											: val === "unpaid"
												? "Unpaid"
												: "Pending";
									}}
								>
									<MenuItem value="">All Payment Status</MenuItem>
									<MenuItem value="paid">Paid</MenuItem>
									<MenuItem value="unpaid">Unpaid</MenuItem>
								</Select>
							</FormControl>

							{/* Payment Method Filter */}
							<FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
								<Select
									value={paymentMethodFilter}
									onChange={(e) => setPaymentMethodFilter(e.target.value)}
									displayEmpty
									renderValue={(val) => {
										if (!val) return "All Payment Methods";
										const found = PAYMENT_METHODS.find((m) => m.value === val);
										return found ? found.label : val;
									}}
								>
									<MenuItem value="">All Payment Methods</MenuItem>
									{PAYMENT_METHODS.map((m) => (
										<MenuItem key={m.value} value={m.value}>
											{m.label}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							{/* Clear Filters */}
							{hasActiveFilters && (
								<Button
									size="small"
									color="error"
									onClick={handleClearFilters}
									sx={{ textTransform: "none", fontWeight: 600 }}
								>
									Clear
								</Button>
							)}
						</Box>
					</Box>

					{/* Tickets Table */}
					<TicketListTable
						data={ticketsData?.data || []}
						isLoading={isTicketsLoading}
						paginationSkip={ticketPagination.skip}
						onScanClick={(ticket) =>
							setTicketScanModal({ open: true, ticket })
						}
						onEditClick={(ticket) =>
							setUpdateTicketModal({ open: true, ticket })
						}
						onDeleteClick={(ticket) =>
							setDeleteTicketModal({ open: true, ticket })
						}
					/>

					{/* Pagination */}
					{ticketsData && ticketsData.total > 0 && (
						<Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
							<CommonPagination
								total={ticketsData.total}
								dataShowPerPage={ticketPagination.limit}
								paginationCalFun={(limit, skip) =>
									setTicketPagination({ limit, skip })
								}
							/>
						</Box>
					)}
				</Card>
			</Container>

			{/* Ticket-wise Manual Scan Modal */}
			<TicketWiseScanModal
				open={ticketScanModal.open}
				ticket={ticketScanModal.ticket}
				handleClose={() => setTicketScanModal({ open: false, ticket: null })}
			/>

			{/* Update Ticket Modal */}
			{updateTicketModal.open && (
				<UpdateTicketModal
					open={updateTicketModal.open}
					ticket={updateTicketModal.ticket}
					handleClose={() => setUpdateTicketModal({ open: false, ticket: null })}
				/>
			)}

			{/* Delete Ticket Modal */}
			{deleteTicketModal.open && (
				<DeleteTicketModal
					open={deleteTicketModal.open}
					ticket={deleteTicketModal.ticket}
					handleClose={() => setDeleteTicketModal({ open: false, ticket: null })}
				/>
			)}
		</>
	);
};

export default TicketListPage;
